/**
 * Modern AVOD plugin — IMA/VAST/VMAP with AdsManager wiring.
 * Refuses to request ads unless modern flags resolve enabled (lab or dual opt-in).
 * Never replaces the content player; overlay client only when activated.
 * Mid-roll breaks fire at schedule offsets; pre at ready; post on content ended.
 */

import { createPlugin } from "../Plugins/createPlugin";
import { createImaVideoAdClient } from "./ImaVideoAdClient";
import { normalizeVmapSchedule } from "./VmapSchedule";
import {
  isLiveImaAdsEnabled,
  resolveModernVideoAdsFlags,
} from "./modernVideoAdsFlags";
import { VideoAdLifecycle } from "./VideoAdLifecycle";
import { createVideoAdMeasurement } from "./VideoAdMeasurement";
import { PlaybackPlatformEvent } from "../Core/PlaybackPlatformEvents";
import { AdBreakKind } from "../Core/EntitlementModel";

/**
 * @param {object} [options]
 * @param {object} [options.features]
 * @param {object} [options.avod]
 * @param {boolean} [options.forceLab]
 * @param {boolean} [options.isLive]
 * @param {object} [options.schedule] breaks / vmap / vast
 * @param {HTMLElement} [options.adContainer]
 * @param {string} [options.contentId]
 * @returns {object} plugin
 */
export function createModernAvodPlugin(options) {
  options = options || {};
  var flags = resolveModernVideoAdsFlags(options);
  var client = null;
  var bus = null;
  var schedule = normalizeVmapSchedule(options.schedule || options.avod || {});
  var measurement = createVideoAdMeasurement({
    enabled:
      flags.enabled &&
      !!(
        options.avod &&
        options.avod.modern &&
        options.avod.modern.measurement_enabled
      ),
    contentId: options.contentId || null,
  });
  var midrollTimers = [];
  var firedBreakIds = {};
  var videoEl = null;

  function publish(name, payload) {
    if (bus && bus.publish) {
      bus.publish(name, payload || {});
    }
  }

  function placementForKind(kind) {
    if (kind === AdBreakKind.MID_ROLL) return "player_midroll";
    if (kind === AdBreakKind.POST_ROLL) return "player_postroll";
    return "player_preroll";
  }

  function requestBreak(breakRow) {
    if (!client || !breakRow) return;
    var id = breakRow.id || "break";
    if (firedBreakIds[id]) return;
    firedBreakIds[id] = true;
    measurement.setContext({
      placementKey: placementForKind(breakRow.kind),
      breakId: id,
      breakKind: breakRow.kind,
      campaignId: breakRow.campaignId || breakRow.campaign_id || null,
      creativeId: breakRow.creativeId || breakRow.creative_id || null,
    });
    client.requestAds({
      vastTagUrl: breakRow.vastTagUrl || schedule.vastTagUrl,
      vmapTagUrl:
        breakRow.vmapTagUrl ||
        (breakRow.kind === AdBreakKind.PRE_ROLL ? schedule.vmapTagUrl : null),
      breaks: [breakRow],
    });
  }

  function clearMidrollWatch() {
    for (var i = 0; i < midrollTimers.length; i++) {
      clearInterval(midrollTimers[i]);
    }
    midrollTimers = [];
  }

  function watchMidrolls() {
    clearMidrollWatch();
    if (!videoEl) return;
    var mids = (schedule.breaks || []).filter(function (b) {
      return b.kind === AdBreakKind.MID_ROLL && b.offsetSeconds != null;
    });
    if (!mids.length) return;

    var timer = setInterval(function () {
      if (!videoEl || videoEl.paused) return;
      var t = videoEl.currentTime || 0;
      for (var i = 0; i < mids.length; i++) {
        var b = mids[i];
        if (firedBreakIds[b.id]) continue;
        if (t >= Number(b.offsetSeconds)) {
          requestBreak(b);
        }
      }
    }, 500);
    midrollTimers.push(timer);
  }

  return createPlugin({
    name: "modern-avod",
    version: "1.0.0",
    onRegister: function (ctx) {
      bus = ctx.bus || (ctx.engine && ctx.engine.getEventBus && ctx.engine.getEventBus());
    },
    onReady: function (ctx) {
      bus = ctx.bus || bus;

      if (options.isLive && !isLiveImaAdsEnabled(options)) {
        publish(VideoAdLifecycle.AD_NO_FILL, {
          reason: "live_ima_disabled",
        });
        return;
      }

      if (!flags.enabled) {
        publish(VideoAdLifecycle.AD_NO_FILL, {
          reason: flags.reason || "modern_video_ads_disabled",
        });
        return;
      }

      videoEl =
        options.contentVideoElement ||
        (ctx.engine && ctx.engine.getVideoElement && ctx.engine.getVideoElement());

      var adContainer = options.adContainer;
      if (!adContainer && videoEl && videoEl.parentElement) {
        adContainer = document.createElement("div");
        adContainer.className = "tpg-ima-ad-container";
        adContainer.setAttribute("data-tpg-ima-container", "1");
        adContainer.style.position = "absolute";
        adContainer.style.inset = "0";
        adContainer.style.zIndex = "6";
        // Idle: pass clicks through to content video. Ad breaks toggle to auto.
        adContainer.style.pointerEvents = "none";
        var parent = videoEl.parentElement;
        if (parent && getComputedStyle(parent).position === "static") {
          parent.style.position = "relative";
        }
        parent.appendChild(adContainer);
      }

      client = createImaVideoAdClient({
        features: options.features,
        avod: options.avod,
        forceLab: options.forceLab,
        adContainer: adContainer,
        contentVideoElement: videoEl,
        onEvent: function (name, payload) {
          measurement.handle(name, payload);
          publish(name, payload);
          if (name === VideoAdLifecycle.AD_BREAK_START) {
            publish(PlaybackPlatformEvent.AD_BREAK_START, payload);
          }
          if (name === VideoAdLifecycle.AD_BREAK_END) {
            publish(PlaybackPlatformEvent.AD_BREAK_END, payload);
          }
          // House / decisioning fallback signal after no-fill (content continues).
          if (name === VideoAdLifecycle.AD_NO_FILL) {
            publish(VideoAdLifecycle.AD_FALLBACK, {
              reason: (payload && payload.reason) || "no_fill",
              mode: "content_continue",
            });
          }
        },
      });

      client.init().then(function () {
        // Prefer opaque VMAP when present (covers full pod schedule).
        if (schedule.vmapTagUrl) {
          measurement.setContext({
            placementKey: "player_preroll",
            breakKind: AdBreakKind.PRE_ROLL,
            breakId: "vmap",
          });
          return client.requestAds({
            vmapTagUrl: schedule.vmapTagUrl,
            vastTagUrl: schedule.vastTagUrl,
            breaks: schedule.breaks,
          });
        }

        var pre = (schedule.breaks || []).filter(function (b) {
          return b.kind === AdBreakKind.PRE_ROLL;
        })[0];
        if (pre || schedule.vastTagUrl) {
          requestBreak(
            pre || {
              id: "pre-default",
              kind: AdBreakKind.PRE_ROLL,
              vastTagUrl: schedule.vastTagUrl,
            }
          );
        }

        watchMidrolls();

        if (videoEl) {
          videoEl.addEventListener("ended", function onEnded() {
            var post = (schedule.breaks || []).filter(function (b) {
              return b.kind === AdBreakKind.POST_ROLL;
            })[0];
            if (post) requestBreak(post);
          });
        }
      });
    },
    onDestroy: function () {
      clearMidrollWatch();
      if (client && client.destroy) {
        client.destroy();
      }
      client = null;
      bus = null;
      videoEl = null;
    },
  });
}

export default createModernAvodPlugin;
